import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Checkbox } from '@components/checkbox';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { useTasklistTemplates } from 'api/templates/requests';
import { Box } from 'components/Box';
import { CardListHeader, CardListItems } from 'components/CardList';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useStore } from 'models/Provider';

function Tasklist({ application }) {
  const { t } = useStore();
  const { showModal } = useModal();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const { hasRolePermission } = usePermissions();
  const tasklistPerm = hasRolePermission('application:edit-tasklist');
  const { tasklist } = application;
  const tasklistQuery = useTasklistTemplates();

  const templates = useMemo(() => {
    let templates = [];
    if (tasklistQuery.data) {
      tasklistQuery.data.forEach((item) => {
        templates.push({
          label: item.name,
          value: item.id,
        });
      });
    }

    if (tasklistPerm) {
      templates.push({
        label: t('application.onboarding.tasklist.create'),
        value: -1,
      });
    }
    return templates;
  }, [t, tasklistPerm, tasklistQuery.data]);

  const resetTemplateChange = useCallback(() => {
    if (tasklist && tasklist.name) {
      templates.forEach((template) => {
        if (template.display === tasklist.name) {
          setSelected(template.value);
        }
      });
    }
  }, [tasklist, templates]);

  useEffect(() => {
    application.loadTasklist();
  }, [application]);

  useEffect(() => {
    resetTemplateChange();
  }, [tasklist, templates.length, resetTemplateChange]);

  function changeTemplateConfirm(templateId) {
    if (!templateId) {
      showModal('CUSTOM_CONFIRM', {
        _container: {
          autoWidth: true,
        },
        data: {
          text: t('application.onboarding.removetemplate.text'),
        },
        confirm: application.removeTasklist,
      });
      return;
    }
    setSelected(templateId);
    if (templateId === selected) {
      return;
    }
    if (templateId < 0) {
      changeTemplate(templateId);
      return;
    }
    showModal('CUSTOM_CONFIRM', {
      data: {
        id: templateId,
        text: t('application.onboarding.changetemplate.text'),
      },
      confirm: changeTemplate,
      onCancel: resetTemplateChange,
    });
  }

  function changeTemplate(templateId) {
    if (templateId === -1) {
      navigate('/settings/templates/tasks/new');
      return;
    }
    if (templateId < 0) return true;
    return application.changeTasklist(templateId);
  }

  const selectedTasklist = tasklist && templates.find((t) => t.label === tasklist.name);
  return (
    <CardList>
      <CardListHeader>
        <Text font="bold14" text={t('application.onboarding.tasklist.heading')} />
      </CardListHeader>
      <Box ml="8" mr="8" mb="8" flex>
        {(templates.length > 0 || tasklistPerm) && (
          <Box grow>
            <Select
              isClearable={tasklist}
              key={selected}
              placeholder={t('application.onboarding.tasklist.choose')}
              defaultValue={selectedTasklist && selectedTasklist.value}
              options={templates}
              onChange={tasklist ? changeTemplateConfirm : changeTemplate}
            />
          </Box>
        )}
      </Box>
      {tasklist && (
        <Box>
          <CardListItems>
            {tasklist.orderedTasks.map((item) => (
              <Task key={item.id} onClick={tasklistPerm ? item.toggle : null}>
                <Stack space={2} orientation="horizontal" align="center">
                  <Checkbox value={item.checked} />
                  <Text font="regular14" text={item.text} />
                </Stack>
              </Task>
            ))}
          </CardListItems>
        </Box>
      )}
    </CardList>
  );
}

export default observer(Tasklist);

const Task = styled.div((p) => ({
  alignItems: 'center',
  backgroundColor: p.theme.colors.white100,
  borderRadius: '0.25rem',
  display: 'flex',
  minHeight: '3rem',
  padding: '0.5rem 0.75rem',
  width: '100%',
  ':hover': {
    cursor: 'pointer',
  },
}));

const CardList = styled.div((p) => ({
  position: 'relative',
  flexShrink: 0,
  ...(p.noHeader && {
    paddingTop: p.theme.spacing.m8,
  }),
  backgroundColor: p.theme.colors.purpleTrans5,
  borderRadius: '0.25rem',
  borderColor: 'transparent',
  borderWidth: '0.125rem',
  borderStyle: 'dashed',
  height: 'auto',
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',

  ...(p.transparent && {
    backgroundColor: 'transparent',
    borderColor: p.theme.colors.purpleTrans10,
  }),
}));
